import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Plus, Trash2, X } from "lucide-react";
import {
  CreateBorrowerData,
  DEFAULT_ENTITY_TYPE,
  ENTITY_TYPES,
  MerchantBankAccount,
} from "@/types/borrower";
import { ExtractedApplicationFields } from "@/types/extraction";
import { useBorrowerDetail } from "@/hooks/useBorrowerDetail";
import { useCreateBorrower } from "@/hooks/queries";
import { canUseAgent } from "@/auth/capabilities";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useTrialAccount } from "@/hooks/useTrialAccount";
import TrialBadge from "@/components/trial/TrialBadge";
import { useStatements } from "@/hooks/useStatements";
import { queryKeys } from "@/queryKeys";
import { borrowerService } from "@/services/borrowers";
import FormPageSkeleton from "@/components/common/FormPageSkeleton";
import AppFileManager from "@/components/application/AppFileManager";
import ApplicationPdfUpload from "@/components/application/ApplicationPdfUpload";
import { formatEIN, normalizeEIN } from "@/utils/einFormatter";
import { formatSSN, isMaskedSSN, normalizeSSN, ssnForSubmit } from "@/utils/ssnFormatter";
import { splitPhone, formatPhoneNumber, combinePhone } from "@/utils/phoneFormatter";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldBorder, fieldBorderInputClassName } from "@/components/ui/field-border";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import StatementPreviewDialog, {
  type PreviewDocument,
} from "@/components/application/StatementPreviewDialog";

const PLAID_ASSET_DOC_KEY = "plaid-asset-report";

/** A date the application parser can return, as the value a date input accepts. */
function extractedDate(value: string | undefined): string {
  const trimmed = (value ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!match) return "";
  return `${match[3]}-${match[1].padStart(2, "0")}-${match[2].padStart(2, "0")}`;
}

interface BorrowerFormProps {
  embedded?: boolean;
}

/** Editable row in the multi-account Bank Accounts section. */
interface EditableBankAccount {
  localKey: string;
  id: number | null; // null = not yet saved
  source: "manual" | "plaid";
  is_primary: boolean;
  bank_name: string;
  name_on_account: string;
  routing_number: string;
  account_number: string;
  account_mask: string;
  account_subtype: string;
  is_tokenized_account_number: boolean;
}

let bankAccountLocalKeySeq = 0;
const nextBankAccountLocalKey = () => `bank-account-${++bankAccountLocalKeySeq}`;

const emptyBankAccountRow = (isPrimary: boolean): EditableBankAccount => ({
  localKey: nextBankAccountLocalKey(),
  id: null,
  source: "manual",
  is_primary: isPrimary,
  bank_name: "",
  name_on_account: "",
  routing_number: "",
  account_number: "",
  account_mask: "",
  account_subtype: "",
  is_tokenized_account_number: false,
});

const toEditableBankAccount = (account: MerchantBankAccount): EditableBankAccount => ({
  localKey: `saved-${account.id}`,
  id: account.id,
  source: account.source,
  is_primary: account.is_primary,
  bank_name: account.bank_name ?? "",
  name_on_account: account.name_on_account ?? "",
  routing_number: account.routing_number ?? "",
  account_number: account.account_number ?? "",
  account_mask: account.account_mask ?? "",
  account_subtype: account.account_subtype ?? "",
  is_tokenized_account_number: account.is_tokenized_account_number,
});

const bankAccountRowHasData = (row: EditableBankAccount): boolean =>
  Boolean(row.bank_name || row.name_on_account || row.routing_number || row.account_number);

const BorrowerForm: React.FC<BorrowerFormProps> = ({ embedded = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const borrowerId = id ? parseInt(id) : null;

  const queryClient = useQueryClient();
  const { borrower, loading, error: borrowerError, patch } = useBorrowerDetail(borrowerId);
  const { create, error: createError } = useCreateBorrower();
  const { user } = useCurrentUser();
  const { isTrial, recipientEmail: trialRecipientEmail, redirectMessage } = useTrialAccount();
  const availableOrganizations = user?.organizations ?? [];
  const organizationIdsKey = availableOrganizations.map((org) => org.id).join(",");
  const showFunderSelection = !isEdit && availableOrganizations.length > 1;
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<number[]>([]);

  useEffect(() => {
    if (!showFunderSelection || !organizationIdsKey) return;
    setSelectedOrganizationIds(organizationIdsKey.split(",").map(Number));
  }, [showFunderSelection, organizationIdsKey]);

  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const {
    documents,
    uploading: statementsUploading,
    error: statementError,
    pendingFiles,
    pendingDeletes,
    pendingDocTypes,
    hasPendingChanges,
    addPendingFiles,
    removePendingFile,
    addPendingDelete,
    removePendingDelete,
    setPendingDocType,
    save: saveStatements,
    discard: discardStatements,
    reprocess: reprocessStatement,
  } = useStatements(borrowerId);

  const [previewActiveKey, setPreviewActiveKey] = useState<string | null>(null);
  const handleViewDocument = (documentId: number) => setPreviewActiveKey(`doc-${documentId}`);
  const handleClosePreview = () => setPreviewActiveKey(null);
  const [plaidPdfRetrying, setPlaidPdfRetrying] = useState(false);
  const [plaidAuthFetching, setPlaidAuthFetching] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const previewDocuments = useMemo((): PreviewDocument[] => {
    const docs: PreviewDocument[] = (documents ?? []).map((d) => ({
      key: `doc-${d.id}`,
      filename: d.filename,
      file_url: d.file_url ?? null,
    }));
    if (borrower?.app_status === "plaid_completed") {
      docs.push({
        key: PLAID_ASSET_DOC_KEY,
        filename: "Plaid Asset Report.pdf",
        file_url: borrower.plaid_asset_report_pdf_url ?? null,
      });
    }
    return docs;
  }, [documents, borrower?.app_status, borrower?.plaid_asset_report_pdf_url]);

  const handlePlaidPdfRetry = useCallback(async () => {
    if (borrowerId == null) return;
    setFormError(null);
    try {
      await borrowerService.plaidRetryPdf(borrowerId);
      setPlaidPdfRetrying(true);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      const detail = ax.response?.data?.detail;
      setFormError(typeof detail === "string" ? detail : "Could not queue Plaid PDF download.");
    }
  }, [borrowerId]);

  useEffect(() => {
    if (!plaidPdfRetrying || borrowerId == null) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 45;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await borrowerService.getById(borrowerId);
        if (cancelled) return;
        queryClient.setQueryData(queryKeys.borrower(borrowerId), data);
        if (data.plaid_asset_report_pdf_url) {
          setPlaidPdfRetrying(false);
        } else if (attempts >= maxAttempts) {
          setPlaidPdfRetrying(false);
          setFormError("Plaid PDF is still not available. Wait a moment and tap Retry again.");
        }
      } catch {
        if (!cancelled && attempts >= maxAttempts) {
          setPlaidPdfRetrying(false);
        }
      }
    };

    void tick();
    const intervalId = window.setInterval(tick, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [plaidPdfRetrying, borrowerId, queryClient]);

  const canFetchPlaidAuth = useMemo(() => {
    const institutions = borrower?.plaid_institutions ?? [];
    return institutions.some(
      (it) =>
        it.status === "active" &&
        (it.auth_status === "pending" || it.auth_status === "failed" || !it.auth_status)
    );
  }, [borrower?.plaid_institutions]);

  const handlePlaidFetchAuth = useCallback(async () => {
    if (borrowerId == null) return;
    setFormError(null);
    setSuccessMessage(null);
    try {
      const result = await borrowerService.plaidFetchAuth(borrowerId);
      setPlaidAuthFetching(true);
      setSuccessMessage(
        `Fetching account numbers from ${result.item_count} connected bank${
          result.item_count === 1 ? "" : "s"
        }\u2026`
      );
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      const detail = ax.response?.data?.detail;
      setFormError(typeof detail === "string" ? detail : "Could not queue Plaid Auth fetch.");
    }
  }, [borrowerId]);

  useEffect(() => {
    if (!plaidAuthFetching || borrowerId == null) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await borrowerService.getById(borrowerId);
        if (cancelled) return;
        queryClient.setQueryData(queryKeys.borrower(borrowerId), data);
        const stillPending = (data.plaid_institutions ?? []).some(
          (it) => it.status === "active" && it.auth_status === "pending"
        );
        if (!stillPending) {
          setPlaidAuthFetching(false);
          const readyCount = (data.plaid_institutions ?? []).filter(
            (it) => it.status === "active" && it.auth_status === "ready"
          ).length;
          const failed = (data.plaid_institutions ?? []).filter(
            (it) => it.status === "active" && it.auth_status === "failed"
          );
          const unsupported = (data.plaid_institutions ?? []).filter(
            (it) => it.status === "active" && it.auth_status === "unsupported"
          );
          if (readyCount > 0) {
            setSuccessMessage(
              `Account numbers retrieved for ${readyCount} bank${readyCount === 1 ? "" : "s"}.`
            );
          } else if (failed.length > 0) {
            setFormError(
              `Plaid Auth failed${
                failed[0]?.auth_error_code ? ` (${failed[0].auth_error_code})` : ""
              }. The merchant may need to reconnect or grant Auth permissions.`
            );
            setSuccessMessage(null);
          } else if (unsupported.length > 0) {
            setFormError(
              "This bank does not support returning account/routing numbers via Plaid Auth. Enter them manually."
            );
            setSuccessMessage(null);
          } else {
            setSuccessMessage(null);
          }
        } else if (attempts >= maxAttempts) {
          setPlaidAuthFetching(false);
          setSuccessMessage(null);
          setFormError("Auth fetch is still running. Wait a moment and refresh, or try again.");
        }
      } catch {
        if (!cancelled && attempts >= maxAttempts) {
          setPlaidAuthFetching(false);
        }
      }
    };

    void tick();
    const intervalId = window.setInterval(tick, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [plaidAuthFetching, borrowerId, queryClient]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<CreateBorrowerData>();

  // Extracted fields that don't map to form inputs: sent with the create, which notes them
  const [extractedExtras, setExtractedExtras] = useState<{
    industry?: string;
    requested_amount?: string;
  } | null>(null);

  // ?? Merchant bank accounts (multi-account; saved via bank-account endpoints) ??
  const [bankAccounts, setBankAccounts] = useState<EditableBankAccount[]>(() => [
    emptyBankAccountRow(true),
  ]);
  const [deletedAccountIds, setDeletedAccountIds] = useState<number[]>([]);
  const [initialBankAccounts, setInitialBankAccounts] = useState<MerchantBankAccount[]>([]);

  const bankAccountsDirty = useMemo(() => {
    if (deletedAccountIds.length > 0) return true;
    const initialById = new Map(initialBankAccounts.map((a) => [a.id, a]));
    for (const row of bankAccounts) {
      if (row.id == null) {
        if (bankAccountRowHasData(row)) return true;
        continue;
      }
      const orig = initialById.get(row.id);
      if (!orig) return true;
      if (
        row.is_primary !== orig.is_primary ||
        row.bank_name !== (orig.bank_name ?? "") ||
        row.name_on_account !== (orig.name_on_account ?? "") ||
        row.routing_number !== (orig.routing_number ?? "") ||
        row.account_number !== (orig.account_number ?? "")
      ) {
        return true;
      }
    }
    return false;
  }, [bankAccounts, deletedAccountIds, initialBankAccounts]);
  // Read by the refetch effect without re-running it: unsaved bank rows survive a refetch.
  const bankAccountsDirtyRef = useRef(false);
  useEffect(() => {
    bankAccountsDirtyRef.current = bankAccountsDirty;
  }, [bankAccountsDirty]);

  const updateBankAccountRow = useCallback(
    (localKey: string, patchRow: Partial<EditableBankAccount>) => {
      setBankAccounts((prev) =>
        prev.map((row) => (row.localKey === localKey ? { ...row, ...patchRow } : row))
      );
    },
    []
  );

  const setPrimaryRow = useCallback((localKey: string) => {
    setBankAccounts((prev) =>
      prev.map((row) => ({ ...row, is_primary: row.localKey === localKey }))
    );
  }, []);

  const addBankAccountRow = useCallback(() => {
    setBankAccounts((prev) => [...prev, emptyBankAccountRow(prev.length === 0)]);
  }, []);

  const removeBankAccountRow = useCallback((localKey: string) => {
    setBankAccounts((prev) => {
      const target = prev.find((row) => row.localKey === localKey);
      if (!target) return prev;
      if (target.id != null) {
        setDeletedAccountIds((ids) => [...ids, target.id as number]);
      }
      const next = prev.filter((row) => row.localKey !== localKey);
      // Keep exactly one primary among the remaining rows.
      if (target.is_primary && next.length > 0 && !next.some((r) => r.is_primary)) {
        next[0] = { ...next[0], is_primary: true };
      }
      return next;
    });
  }, []);

  /** Persist bank-account changes via the sub-resource endpoints. */
  const syncBankAccounts = useCallback(
    async (targetBorrowerId: number) => {
      const initialById = new Map(initialBankAccounts.map((a) => [a.id, a]));

      for (const accountId of deletedAccountIds) {
        await borrowerService.deleteBankAccount(targetBorrowerId, accountId);
      }

      let primaryAccountId: number | null = null;
      let primaryChanged = false;

      for (const row of bankAccounts) {
        if (row.id == null) {
          if (!bankAccountRowHasData(row)) continue;
          const created = await borrowerService.createBankAccount(targetBorrowerId, {
            bank_name: row.bank_name,
            name_on_account: row.name_on_account,
            routing_number: row.routing_number,
            account_number: row.account_number,
          });
          if (row.is_primary) {
            primaryAccountId = created.id;
            primaryChanged = !created.is_primary;
          }
          continue;
        }

        const orig = initialById.get(row.id);
        // Plaid-sourced rows are read-only in the UI (name, bank, routing, account
        // all come from Plaid). Manual rows stay fully editable.
        const editableFields: (keyof EditableBankAccount & keyof MerchantBankAccount)[] =
          row.source === "plaid"
            ? []
            : ["bank_name", "name_on_account", "routing_number", "account_number"];
        const patchData: Record<string, string> = {};
        for (const field of editableFields) {
          const value = String(row[field] ?? "");
          if (!orig || value !== String(orig[field] ?? "")) {
            patchData[field] = value;
          }
        }
        if (Object.keys(patchData).length > 0) {
          await borrowerService.updateBankAccount(targetBorrowerId, row.id, patchData);
        }
        if (row.is_primary && (!orig || !orig.is_primary)) {
          primaryAccountId = row.id;
          primaryChanged = true;
        }
      }

      if (primaryChanged && primaryAccountId != null) {
        await borrowerService.setPrimaryBankAccount(targetBorrowerId, primaryAccountId);
      }

      setDeletedAccountIds([]);
      // Saved: the refetch below may replace the rows.
      bankAccountsDirtyRef.current = false;
      await queryClient.invalidateQueries({ queryKey: queryKeys.borrower(targetBorrowerId) });
      // Contracts tab's bank-detail fields (name/routing/account number) come
      // solely from this cache, with no borrower fallback, so it must be
      // invalidated too or they'll keep showing the pre-edit account.
      await queryClient.invalidateQueries({ queryKey: ["contractForm", targetBorrowerId] });
    },
    [bankAccounts, deletedAccountIds, initialBankAccounts, queryClient]
  );

  const handleFieldsExtracted = useCallback(
    (fields: ExtractedApplicationFields) => {
      if (fields.company_name) setValue("company_name", fields.company_name, { shouldDirty: true });
      if (fields.owner_name) setValue("owner_name", fields.owner_name, { shouldDirty: true });
      if (fields.ein) setValue("ein", formatEIN(fields.ein), { shouldDirty: true });
      if (fields.ssn) setValue("owner_ssn", formatSSN(fields.ssn), { shouldDirty: true });
      const dateOfBirth = extractedDate(fields.date_of_birth);
      if (dateOfBirth) setValue("owner_date_of_birth", dateOfBirth, { shouldDirty: true });
      setValue(
        "entity_type",
        (ENTITY_TYPES as readonly string[]).includes(fields.entity_type ?? "")
          ? fields.entity_type
          : DEFAULT_ENTITY_TYPE,
        { shouldDirty: true }
      );
      if (fields.email) setValue("email", fields.email, { shouldDirty: true });
      if (fields.phone) {
        const { countryCode, number } = splitPhone(fields.phone);
        setValue("phone_country_code", countryCode, { shouldDirty: true });
        setValue("phone_number", formatPhoneNumber(number), { shouldDirty: true });
      }
      if (fields.owner_address)
        setValue("address_line1", fields.owner_address, { shouldDirty: true });
      if (fields.owner_city) setValue("city", fields.owner_city, { shouldDirty: true });
      if (fields.owner_state_code)
        setValue("state", fields.owner_state_code, { shouldDirty: true });
      if (fields.owner_zip_code)
        setValue("postal_code", fields.owner_zip_code, { shouldDirty: true });
      if (fields.business_address)
        setValue("business_address_line1", fields.business_address, { shouldDirty: true });
      if (fields.business_city)
        setValue("business_city", fields.business_city, { shouldDirty: true });
      if (fields.business_state_code)
        setValue("business_state", fields.business_state_code, { shouldDirty: true });
      if (fields.business_zip_code)
        setValue("business_postal_code", fields.business_zip_code, { shouldDirty: true });

      setExtractedExtras({
        industry: fields.industry,
        requested_amount: fields.requested_amount,
      });
    },
    [setValue]
  );

  useEffect(() => {
    if (isEdit && borrower) {
      // A refetch (another tab's save, an approved assistant edit) updates the fields the user
      // hasn't touched and keeps what they are typing.
      reset(
        {
          company_name: borrower.company_name,
          owner_name: borrower.owner_name,
          email: borrower.email ?? "",
          ...(() => {
            const { countryCode, number } = borrower.phone
              ? splitPhone(borrower.phone)
              : { countryCode: "1", number: "" };
            return { phone_country_code: countryCode, phone_number: formatPhoneNumber(number) };
          })(),
          ein: borrower.ein ? formatEIN(borrower.ein) : "",
          entity_type: borrower.entity_type ?? DEFAULT_ENTITY_TYPE,
          address_line1: borrower.address_line1 ?? "",
          address_line2: borrower.address_line2 ?? "",
          city: borrower.city ?? "",
          state: borrower.state ? borrower.state.toUpperCase().slice(0, 2) : "",
          postal_code: borrower.postal_code ?? "",
          business_address_line1: borrower.business_address_line1 ?? "",
          business_city: borrower.business_city ?? "",
          business_state: borrower.business_state
            ? borrower.business_state.toUpperCase().slice(0, 2)
            : "",
          business_postal_code: borrower.business_postal_code ?? "",
          merchants_dba: borrower.merchants_dba ?? "",
          owner_drivers_license: borrower.owner_drivers_license ?? "",
          owner_date_of_birth: borrower.owner_date_of_birth ?? "",
          owner_ssn: borrower.owner_ssn ? formatSSN(borrower.owner_ssn) : "",
        },
        { keepDirtyValues: true }
      );
      // Bank rows being edited (typed, added or deleted) are kept the same way.
      if (!bankAccountsDirtyRef.current) {
        const accounts = borrower.bank_accounts ?? [];
        setInitialBankAccounts(accounts);
        setDeletedAccountIds([]);
        setBankAccounts(
          accounts.length > 0 ? accounts.map(toEditableBankAccount) : [emptyBankAccountRow(true)]
        );
      }
    }
  }, [borrower, isEdit, reset]);

  const onSubmit = async (data: CreateBorrowerData) => {
    setFormError(null);
    if (!isEdit && pendingFiles.length < 1) {
      setFormError("At least one file is required to create an application.");
      return;
    }
    if (showFunderSelection && selectedOrganizationIds.length === 0) {
      setFormError("Select at least one funder to submit this application.");
      return;
    }
    const invalidRouting = bankAccounts.some(
      (row) =>
        bankAccountRowHasData(row) &&
        row.routing_number.trim() !== "" &&
        !/^\d{9}$/.test(row.routing_number.trim())
    );
    if (invalidRouting) {
      setFormError("Routing numbers must be 9 digits.");
      return;
    }
    const normalized: CreateBorrowerData = {
      ...data,
      owner_ssn: ssnForSubmit(data.owner_ssn),
      phone: combinePhone(data.phone_country_code ?? "", data.phone_number ?? "") || undefined,
      phone_country_code: undefined,
      phone_number: undefined,
      organization_ids: showFunderSelection ? selectedOrganizationIds : undefined,
      // What the PDF gave that the form has no input for (industry, requested amount): the
      // create writes it as the application's "[Extracted from Application PDF]" note, as the
      // user who created it.
      ...(!isEdit && extractedExtras
        ? {
            extracted_industry: extractedExtras.industry,
            extracted_requested_amount: extractedExtras.requested_amount,
          }
        : {}),
    };

    setIsSubmittingForm(true);
    try {
      if (isEdit && borrowerId) {
        await patch(normalized);
        // Saved: nothing is unsaved any more, so the refetch that follows sets every field.
        reset(getValues());
        if (bankAccountsDirty) {
          await syncBankAccounts(borrowerId);
        }
        if (hasPendingChanges) {
          await saveStatements();
        }
        if (!embedded) {
          navigate(`/applications/${borrowerId}`);
        } else {
          setSuccessMessage("Application Info Updated");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } else {
        const result = await create(normalized);
        if (bankAccountsDirty) {
          await syncBankAccounts(result.id);
        }
        if (hasPendingChanges) {
          await saveStatements(result.id);
        }
        const funderCount = showFunderSelection ? selectedOrganizationIds.length : 1;
        navigate(`/applications/${result.id}`, {
          state: funderCount > 1 ? { multiFunderCreatedCount: funderCount } : undefined,
        });
      }
    } catch (err: any) {
      setFormError(err.message || "Failed to save application");
    } finally {
      setIsSubmittingForm(false);
    }
  };

  if (isEdit && loading && !borrower) {
    return <FormPageSkeleton fieldCount={6} />;
  }

  const isSubmitting = isSubmittingForm;
  const displayError = borrowerError || createError;

  return (
    <div>
      {!embedded && (
        <div className="mb-6 flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(isEdit && id ? `/applications/${id}` : "/applications")}
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-semibold tracking-tight">
            {isEdit ? "Edit Application" : "New Application"}
          </h1>
        </div>
      )}

      {displayError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{displayError}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert className="relative mb-4 border-green-600/50 bg-green-50 pr-10 text-green-900 dark:bg-green-950 dark:text-green-100">
          <AlertDescription>{successMessage}</AlertDescription>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 h-6 w-6"
            onClick={() => setSuccessMessage(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </Alert>
      )}

      {formError && (
        <Alert className="relative mb-4 border-amber-500/50 bg-amber-50 pr-10 text-amber-900 dark:bg-amber-950 dark:text-amber-100">
          <AlertDescription>{formError}</AlertDescription>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 h-6 w-6"
            onClick={() => setFormError(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </Alert>
      )}

      <Card>
        <CardContent className="p-6 pt-6">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
              <div className="min-w-0 flex-1 space-y-6">
                {!isEdit && <ApplicationPdfUpload onFieldsExtracted={handleFieldsExtracted} />}
                <div>
                  {showFunderSelection && (
                    <div className="mb-6">
                      <h2 className="mb-3 text-lg font-semibold">Send to funders</h2>
                      <p className="mb-3 text-sm text-muted-foreground">
                        Selecting multiple funders creates one application per funder. You will open
                        the first application after submit; the others appear in each funder&apos;s
                        queue.
                      </p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {availableOrganizations.map((org) => {
                          const checked = selectedOrganizationIds.includes(org.id);
                          return (
                            <label
                              key={org.id}
                              className="flex cursor-pointer items-center gap-2 rounded-md border border-border/60 px-3 py-2"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(value) => {
                                  setSelectedOrganizationIds((current) => {
                                    if (value) {
                                      return current.includes(org.id)
                                        ? current
                                        : [...current, org.id];
                                    }
                                    return current.filter((id) => id !== org.id);
                                  });
                                }}
                              />
                              <span className="text-sm">{org.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <h2 className="mb-4 text-lg font-semibold">Business Information</h2>
                  <div className="grid gap-4">
                    <div className="grid grid-cols-[1fr_15%] gap-4">
                      <div className="space-y-2">
                        <FieldBorder
                          label="Company Name"
                          htmlFor="company_name"
                          invalid={!!errors.company_name}
                        >
                          <Input
                            id="company_name"
                            className={fieldBorderInputClassName}
                            {...register("company_name", {
                              required: "Company name is required",
                            })}
                            aria-invalid={!!errors.company_name}
                          />
                        </FieldBorder>
                        {errors.company_name && (
                          <p className="text-sm text-destructive">{errors.company_name.message}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Controller
                          name="entity_type"
                          control={control}
                          defaultValue={DEFAULT_ENTITY_TYPE}
                          render={({ field: { onChange, value } }) => (
                            <Select value={value ?? DEFAULT_ENTITY_TYPE} onValueChange={onChange}>
                              <FieldBorder label="Entity Type" htmlFor="entity_type">
                                <SelectTrigger
                                  id="entity_type"
                                  className="h-6 border-0 bg-transparent p-0 shadow-none focus:ring-0"
                                >
                                  <SelectValue />
                                </SelectTrigger>
                              </FieldBorder>
                              <SelectContent>
                                {ENTITY_TYPES.map((type) => (
                                  <SelectItem key={type} value={type}>
                                    {type}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <FieldBorder label="DBA (Doing Business As)" htmlFor="merchants_dba">
                          <Input
                            id="merchants_dba"
                            placeholder="Optional"
                            className={fieldBorderInputClassName}
                            {...register("merchants_dba")}
                            aria-invalid={!!errors.merchants_dba}
                          />
                        </FieldBorder>
                      </div>
                      <div className="space-y-2">
                        <Controller
                          name="ein"
                          control={control}
                          rules={{
                            validate: (v) => {
                              const normalized = normalizeEIN(v ?? "");
                              return (
                                !normalized ||
                                normalized.length === 9 ||
                                "EIN must be 9 digits (XX-XXXXXXX)"
                              );
                            },
                          }}
                          render={({ field: { onChange, value, ref } }) => (
                            <FieldBorder label="EIN" htmlFor="ein" invalid={!!errors.ein}>
                              <Input
                                id="ein"
                                placeholder="XX-XXXXXXX"
                                className={fieldBorderInputClassName}
                                ref={ref}
                                value={value ?? ""}
                                onChange={(e) => onChange(formatEIN(e.target.value))}
                                aria-invalid={!!errors.ein}
                              />
                            </FieldBorder>
                          )}
                        />
                        {errors.ein && (
                          <p className="text-sm text-destructive">{errors.ein.message}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <FieldBorder
                          label="Owner Name"
                          htmlFor="owner_name"
                          invalid={!!errors.owner_name}
                        >
                          <Input
                            id="owner_name"
                            className={fieldBorderInputClassName}
                            {...register("owner_name")}
                            aria-invalid={!!errors.owner_name}
                          />
                        </FieldBorder>
                        {errors.owner_name && (
                          <p className="text-sm text-destructive">{errors.owner_name.message}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <FieldBorder label="Email" htmlFor="email" invalid={!!errors.email}>
                          <Input
                            id="email"
                            type="email"
                            className={fieldBorderInputClassName}
                            {...register("email")}
                            aria-invalid={!!errors.email}
                          />
                        </FieldBorder>
                        {errors.email && (
                          <p className="text-sm text-destructive">{errors.email.message}</p>
                        )}
                        {isTrial && (
                          <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                            <TrialBadge
                              label="Trial"
                              tooltip={redirectMessage(borrower?.email)}
                              className="mt-px"
                            />
                            <span>
                              Contracts and Plaid links go to{" "}
                              <span className="font-medium text-foreground">
                                {trialRecipientEmail}
                              </span>{" "}
                              instead of this address. Hover for details.
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <FieldBorder
                          label="Phone"
                          invalid={!!errors.phone_country_code || !!errors.phone_number}
                          className="w-full md:w-1/2"
                        >
                          <div className="flex h-6 items-center gap-2">
                            <div className="flex items-center">
                              <span className="pr-1 text-sm text-muted-foreground">+</span>
                              <Controller
                                name="phone_country_code"
                                control={control}
                                rules={{
                                  validate: (v) => {
                                    const d = (v ?? "").replace(/\D/g, "");
                                    const num = getValues("phone_number") ?? "";
                                    if (num.replace(/\D/g, "") && !d) return "Required";
                                    return true;
                                  },
                                }}
                                render={({ field: { onChange, value, ref } }) => (
                                  <Input
                                    id="phone_country_code"
                                    placeholder="1"
                                    ref={ref}
                                    value={value ?? ""}
                                    onChange={(e) =>
                                      onChange(e.target.value.replace(/\D/g, "").slice(0, 3))
                                    }
                                    aria-invalid={!!errors.phone_country_code}
                                    className={cn(fieldBorderInputClassName, "w-8")}
                                  />
                                )}
                              />
                            </div>
                            <Controller
                              name="phone_number"
                              control={control}
                              rules={{
                                validate: (v) => {
                                  const d = (v ?? "").replace(/\D/g, "");
                                  if (!d) return true;
                                  return d.length === 10 || "Must be 10 digits";
                                },
                              }}
                              render={({ field: { onChange, value, ref } }) => (
                                <Input
                                  id="phone_number"
                                  placeholder="(XXX) XXX-XXXX"
                                  ref={ref}
                                  value={value ?? ""}
                                  onChange={(e) => onChange(formatPhoneNumber(e.target.value))}
                                  aria-invalid={!!errors.phone_number}
                                  className={cn(fieldBorderInputClassName, "flex-1")}
                                />
                              )}
                            />
                          </div>
                        </FieldBorder>
                        {(errors.phone_country_code || errors.phone_number) && (
                          <p className="text-sm text-destructive">
                            {errors.phone_country_code?.message ?? errors.phone_number?.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h2 className="mb-4 text-lg font-semibold">Business Address</h2>
                  <div className="grid gap-4 md:grid-cols-12">
                    <div className="space-y-2 md:col-span-12">
                      <FieldBorder
                        label="Address"
                        htmlFor="business_address_line1"
                        invalid={!!errors.business_address_line1}
                      >
                        <Input
                          id="business_address_line1"
                          placeholder="Street address"
                          className={fieldBorderInputClassName}
                          {...register("business_address_line1")}
                          aria-invalid={!!errors.business_address_line1}
                        />
                      </FieldBorder>
                      {errors.business_address_line1 && (
                        <p className="text-sm text-destructive">
                          {errors.business_address_line1.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-4">
                      <FieldBorder
                        label="City"
                        htmlFor="business_city"
                        invalid={!!errors.business_city}
                      >
                        <Input
                          id="business_city"
                          className={fieldBorderInputClassName}
                          {...register("business_city")}
                          aria-invalid={!!errors.business_city}
                        />
                      </FieldBorder>
                      {errors.business_city && (
                        <p className="text-sm text-destructive">{errors.business_city.message}</p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-4">
                      <Controller
                        name="business_state"
                        control={control}
                        rules={{
                          validate: (v) =>
                            !(v ?? "").trim() ||
                            /^[A-Z]{2}$/.test((v ?? "").trim()) ||
                            "Enter a 2-letter state code (e.g. NY)",
                        }}
                        render={({ field: { onChange, value, ref } }) => (
                          <FieldBorder
                            label="State Code"
                            htmlFor="business_state"
                            invalid={!!errors.business_state}
                          >
                            <Input
                              id="business_state"
                              placeholder="NY"
                              className={fieldBorderInputClassName}
                              ref={ref}
                              value={value ?? ""}
                              onChange={(e) =>
                                onChange(
                                  e.target.value
                                    .replace(/[^a-zA-Z]/g, "")
                                    .toUpperCase()
                                    .slice(0, 2)
                                )
                              }
                              aria-invalid={!!errors.business_state}
                            />
                          </FieldBorder>
                        )}
                      />
                      {errors.business_state && (
                        <p className="text-sm text-destructive">{errors.business_state.message}</p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-4">
                      <FieldBorder
                        label="Postal Code"
                        htmlFor="business_postal_code"
                        invalid={!!errors.business_postal_code}
                      >
                        <Input
                          id="business_postal_code"
                          className={fieldBorderInputClassName}
                          {...register("business_postal_code")}
                          aria-invalid={!!errors.business_postal_code}
                        />
                      </FieldBorder>
                      {errors.business_postal_code && (
                        <p className="text-sm text-destructive">
                          {errors.business_postal_code.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h2 className="mb-4 text-lg font-semibold">Owner Information</h2>
                  <div className="grid gap-4 md:grid-cols-12">
                    <div className="space-y-2 md:col-span-4">
                      <FieldBorder label="Driver's License" htmlFor="owner_drivers_license">
                        <Input
                          id="owner_drivers_license"
                          placeholder="Optional"
                          className={fieldBorderInputClassName}
                          {...register("owner_drivers_license")}
                          aria-invalid={!!errors.owner_drivers_license}
                        />
                      </FieldBorder>
                    </div>
                    <div className="space-y-2 md:col-span-4">
                      <FieldBorder label="Date of birth" htmlFor="owner_date_of_birth">
                        <Input
                          id="owner_date_of_birth"
                          type="date"
                          className={fieldBorderInputClassName}
                          {...register("owner_date_of_birth")}
                          aria-invalid={!!errors.owner_date_of_birth}
                        />
                      </FieldBorder>
                    </div>
                    <div className="space-y-2 md:col-span-4">
                      <Controller
                        name="owner_ssn"
                        control={control}
                        rules={{
                          validate: (value) => {
                            if (!value || isMaskedSSN(value)) return true;
                            const digits = normalizeSSN(value);
                            return digits.length === 9 || "Enter a 9-digit SSN or leave it blank";
                          },
                        }}
                        render={({ field: { onChange, value, ref } }) => (
                          <FieldBorder label="SSN" htmlFor="owner_ssn" invalid={!!errors.owner_ssn}>
                            <Input
                              id="owner_ssn"
                              placeholder="Optional"
                              autoComplete="off"
                              inputMode="numeric"
                              className={fieldBorderInputClassName}
                              ref={ref}
                              value={value ?? ""}
                              onChange={(e) => onChange(formatSSN(e.target.value))}
                              aria-invalid={!!errors.owner_ssn}
                            />
                          </FieldBorder>
                        )}
                      />
                      {errors.owner_ssn && (
                        <p className="text-sm text-destructive">{errors.owner_ssn.message}</p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-12">
                      <FieldBorder
                        label="Address"
                        htmlFor="address_line1"
                        invalid={!!errors.address_line1}
                      >
                        <Input
                          id="address_line1"
                          placeholder="Street address"
                          className={fieldBorderInputClassName}
                          {...register("address_line1")}
                          aria-invalid={!!errors.address_line1}
                        />
                      </FieldBorder>
                      {errors.address_line1 && (
                        <p className="text-sm text-destructive">{errors.address_line1.message}</p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-4">
                      <FieldBorder label="City" htmlFor="city" invalid={!!errors.city}>
                        <Input
                          id="city"
                          className={fieldBorderInputClassName}
                          {...register("city")}
                          aria-invalid={!!errors.city}
                        />
                      </FieldBorder>
                      {errors.city && (
                        <p className="text-sm text-destructive">{errors.city.message}</p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-4">
                      <Controller
                        name="state"
                        control={control}
                        rules={{
                          validate: (v) =>
                            !(v ?? "").trim() ||
                            /^[A-Z]{2}$/.test((v ?? "").trim()) ||
                            "Enter a 2-letter state code (e.g. NY)",
                        }}
                        render={({ field: { onChange, value, ref } }) => (
                          <FieldBorder label="State Code" htmlFor="state" invalid={!!errors.state}>
                            <Input
                              id="state"
                              placeholder="NY"
                              className={fieldBorderInputClassName}
                              ref={ref}
                              value={value ?? ""}
                              onChange={(e) =>
                                onChange(
                                  e.target.value
                                    .replace(/[^a-zA-Z]/g, "")
                                    .toUpperCase()
                                    .slice(0, 2)
                                )
                              }
                              aria-invalid={!!errors.state}
                            />
                          </FieldBorder>
                        )}
                      />
                      {errors.state && (
                        <p className="text-sm text-destructive">{errors.state.message}</p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-4">
                      <FieldBorder
                        label="Postal Code"
                        htmlFor="postal_code"
                        invalid={!!errors.postal_code}
                      >
                        <Input
                          id="postal_code"
                          className={fieldBorderInputClassName}
                          {...register("postal_code")}
                          aria-invalid={!!errors.postal_code}
                        />
                      </FieldBorder>
                      {errors.postal_code && (
                        <p className="text-sm text-destructive">{errors.postal_code.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-lg font-semibold">Bank Accounts ({bankAccounts.length})</h2>
                    <div className="flex flex-wrap items-center gap-2">
                      {isEdit && canFetchPlaidAuth && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void handlePlaidFetchAuth()}
                          disabled={plaidAuthFetching || bankAccountsDirty}
                          title={
                            bankAccountsDirty
                              ? "Save or discard bank account edits before fetching from Plaid"
                              : "Fetch routing and account numbers from connected Plaid banks"
                          }
                        >
                          {plaidAuthFetching ? (
                            <>
                              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                              Fetching?
                            </>
                          ) : (
                            "Get account numbers from Plaid"
                          )}
                        </Button>
                      )}
                      <Button type="button" variant="outline" size="sm" onClick={addBankAccountRow}>
                        <Plus className="mr-1 h-4 w-4" />
                        Add account
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {bankAccounts.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        No bank accounts on file. Add one manually, or they will appear here when
                        the merchant connects a bank via Plaid.
                      </p>
                    )}
                    {bankAccounts.map((row) => {
                      const isPlaid = row.source === "plaid";
                      const fieldId = (name: string) => `${row.localKey}-${name}`;
                      const routingInvalid =
                        row.routing_number.trim() !== "" &&
                        !/^\d{9}$/.test(row.routing_number.trim());
                      return (
                        <div key={row.localKey} className="rounded-md border p-4">
                          <div className="mb-3 flex flex-wrap items-center gap-3">
                            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                              <input
                                type="radio"
                                name="primary_bank_account"
                                checked={row.is_primary}
                                onChange={() => setPrimaryRow(row.localKey)}
                                className="h-4 w-4 accent-primary"
                              />
                              Primary (used on contracts)
                            </label>
                            {isPlaid && (
                              <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-950 dark:text-sky-200">
                                Connected via Plaid
                                {row.account_subtype ? ` \u00b7 ${row.account_subtype}` : ""}
                              </span>
                            )}
                            {isPlaid && row.is_tokenized_account_number && (
                              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                                Tokenized account number
                              </span>
                            )}
                            {!isPlaid && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="ml-auto h-8 w-8 text-muted-foreground hover:text-destructive"
                                onClick={() => removeBankAccountRow(row.localKey)}
                                aria-label="Remove account"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <FieldBorder label="Bank Name" htmlFor={fieldId("bank_name")}>
                                <Input
                                  id={fieldId("bank_name")}
                                  placeholder="Optional"
                                  className={cn(
                                    fieldBorderInputClassName,
                                    isPlaid &&
                                      "cursor-default disabled:text-foreground disabled:opacity-100"
                                  )}
                                  value={row.bank_name}
                                  disabled={isPlaid}
                                  onChange={(e) =>
                                    updateBankAccountRow(row.localKey, {
                                      bank_name: e.target.value,
                                    })
                                  }
                                />
                              </FieldBorder>
                            </div>
                            <div className="space-y-2">
                              <FieldBorder
                                label="Name on Account"
                                htmlFor={fieldId("name_on_account")}
                              >
                                <Input
                                  id={fieldId("name_on_account")}
                                  placeholder="Optional"
                                  className={cn(
                                    fieldBorderInputClassName,
                                    isPlaid &&
                                      "cursor-default disabled:text-foreground disabled:opacity-100"
                                  )}
                                  value={row.name_on_account}
                                  disabled={isPlaid}
                                  onChange={(e) =>
                                    updateBankAccountRow(row.localKey, {
                                      name_on_account: e.target.value,
                                    })
                                  }
                                />
                              </FieldBorder>
                            </div>
                            <div className="space-y-2">
                              <FieldBorder
                                label="Routing Number"
                                htmlFor={fieldId("routing_number")}
                                invalid={routingInvalid}
                              >
                                <Input
                                  id={fieldId("routing_number")}
                                  placeholder="Optional"
                                  className={cn(
                                    fieldBorderInputClassName,
                                    isPlaid &&
                                      "cursor-default disabled:text-foreground disabled:opacity-100"
                                  )}
                                  value={row.routing_number}
                                  disabled={isPlaid}
                                  aria-invalid={routingInvalid}
                                  onChange={(e) =>
                                    updateBankAccountRow(row.localKey, {
                                      routing_number: e.target.value.replace(/\D/g, "").slice(0, 9),
                                    })
                                  }
                                />
                              </FieldBorder>
                              {routingInvalid && (
                                <p className="text-sm text-destructive">
                                  Routing number must be 9 digits
                                </p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <FieldBorder
                                label="Account Number"
                                htmlFor={fieldId("account_number")}
                              >
                                <Input
                                  id={fieldId("account_number")}
                                  placeholder="Optional"
                                  className={cn(
                                    fieldBorderInputClassName,
                                    isPlaid &&
                                      "cursor-default disabled:text-foreground disabled:opacity-100"
                                  )}
                                  value={row.account_number}
                                  disabled={isPlaid}
                                  onChange={(e) =>
                                    updateBankAccountRow(row.localKey, {
                                      account_number: e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 30),
                                    })
                                  }
                                />
                              </FieldBorder>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {!isEdit && (
                  <p className="text-sm text-muted-foreground">
                    Minimum 3 files required to create an application.
                  </p>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  {!embedded && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(`/applications/${id}`)}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      (isEdit && !isDirty && !hasPendingChanges && !bankAccountsDirty) ||
                      (!isEdit && pendingFiles.length < 3)
                    }
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : isEdit ? (
                      "Update Application"
                    ) : pendingFiles.length > 0 ? (
                      "Create & Process"
                    ) : (
                      "Create Application"
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex w-full shrink-0 flex-col lg:w-[40%]">
                <AppFileManager
                  borrowerId={borrowerId}
                  canAddToChat={canUseAgent(user)}
                  documents={documents}
                  uploading={statementsUploading}
                  uploadError={statementError}
                  onDrop={addPendingFiles}
                  onView={handleViewDocument}
                  pendingFiles={pendingFiles}
                  pendingDeletes={pendingDeletes}
                  pendingDocTypes={pendingDocTypes}
                  hasPendingChanges={hasPendingChanges}
                  onRemovePendingFile={removePendingFile}
                  onReprocess={reprocessStatement}
                  onAddPendingDelete={addPendingDelete}
                  onRemovePendingDelete={removePendingDelete}
                  onDocTypeChange={setPendingDocType}
                  onSave={saveStatements}
                  onDiscard={discardStatements}
                  plaidAssetReport={
                    borrower?.app_status === "plaid_completed"
                      ? {
                          visible: true,
                          fileUrl: borrower.plaid_asset_report_pdf_url ?? null,
                          pdfFetchFailed: Boolean(borrower.plaid_pdf_fetch_failed),
                          retrying: plaidPdfRetrying,
                          onView: () => setPreviewActiveKey(PLAID_ASSET_DOC_KEY),
                          onRetry: handlePlaidPdfRetry,
                        }
                      : undefined
                  }
                />
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      <StatementPreviewDialog
        documents={previewDocuments}
        activeKey={previewActiveKey}
        onClose={handleClosePreview}
      />
    </div>
  );
};

export default BorrowerForm;
